import { useCallback, useEffect, useState } from "react";
import { useDropzone, FileRejection, ErrorCode } from "react-dropzone";
import clsx from "clsx";
import { Icon } from "react-migration/components/Icon";
import { ProgressCircle, ProgressCircleColor, ProgressCircleSize } from "react-migration/components/ProgressCircle";
import { KB_B_SIZE, MB_B_SIZE } from "react-migration/constants";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { Button } from "../Button";

export enum FileSizeValue {
  KB = "KB",
  MB = "MB",
}

type MaxFileSize = {
  value: number;
  unit: FileSizeValue;
};

type Dimensions = number | "auto";

export type ImageUploadStatus = "ready" | "uploading" | "error";

export interface ImageUploadProps {
  value?: string; // UploadedFile ID
  onChange: (id: string | undefined) => void;
  onUpload: (file: File) => Promise<string>;
  onDelete: (fileId: string) => Promise<boolean>;
  onStatusChange?: (status: ImageUploadStatus) => void;
  uploadingText?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  maxFileSize?: MaxFileSize;
  height?: Dimensions;
  width?: Dimensions;
}

const DEFAULT_MAX_FILE_SIZE = { value: 500, unit: FileSizeValue.KB };

export const ImageUpload = ({
  value,
  onChange,
  onUpload,
  onDelete,
  onStatusChange,
  uploadingText,
  disabled = false,
  placeholder,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  className,
  height = "auto",
  width = "auto",
}: ImageUploadProps) => {
  const { t } = useTranslation();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const notifyStatus = useCallback(
    (status: ImageUploadStatus) => {
      onStatusChange?.(status);
    },
    [onStatusChange]
  );

  useEffect(() => {
    notifyStatus("ready");
  }, [notifyStatus]);

  useEffect(() => {
    if (value) {
      setPreviewUrl(value);
    } else {
      setPreviewUrl(null);
    }
  }, [value]);

  // Cleanup local blobs
  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      setError(null);
      notifyStatus("ready");

      const generalErrorMessage = t("image_upload.error.something_went_wrong");
      if (fileRejections.length > 0) {
        let errorMessage = "";
        switch (fileRejections[0].errors[0]?.code) {
          case ErrorCode.FileTooLarge:
            errorMessage = t("image_upload.error.file_too_large", {
              max_file_size: maxFileSize.value,
              unit: maxFileSize.unit,
            });
            break;
          case ErrorCode.FileInvalidType:
            errorMessage = t("image_upload.error.invalid_file_type");
            break;
          default:
            errorMessage = generalErrorMessage;
            break;
        }
        setError(errorMessage);
        notifyStatus("error");
        return;
      }

      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      setIsUploading(true);
      notifyStatus("uploading");
      setPreviewUrl(URL.createObjectURL(file));

      try {
        const id = await onUpload(file);
        onChange(id);
        notifyStatus("ready");
      } catch (err) {
        setError(t("image_upload.error.upload_failed"));
        setPreviewUrl(null);
        onChange(undefined);
        notifyStatus("error");
        console.error(err);
      } finally {
        setIsUploading(false);
      }
    },
    [maxFileSize.unit, maxFileSize.value, notifyStatus, onChange, onUpload, t]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/jpeg": [".jpg", ".jpeg"], "image/png": [".png"] },
    maxSize: maxFileSize.value * (maxFileSize.unit === FileSizeValue.KB ? KB_B_SIZE : MB_B_SIZE),
    multiple: false,
    disabled: disabled || isUploading,
    noDrag: previewUrl ? true : false,
    noKeyboard: previewUrl ? true : false,
    noClick: previewUrl ? true : false,
  });

  const handleRemove = useCallback(async () => {
    if (value) {
      try {
        await onDelete(value);
      } catch (err) {
        setError(t("image_upload.error.delete_failed"));
        notifyStatus("error");
        return;
      }
    }
    setPreviewUrl(null);
    setError(null);
    notifyStatus("ready");
    onChange(undefined);
  }, [value, onDelete, onChange, notifyStatus, t]);

  return (
    <div
      {...getRootProps({
        className: clsx(
          "relative",
          (width === "auto" || height === "auto") && "flex-none",
          "flex flex-col items-center justify-center",
          "rounded-lg",
          "group",
          {
            "border-border-action-hover bg-white": !isDragActive && !error,
            "border-platform-blue-200 bg-platform-blue-50": isDragActive,
            "border-red-700 bg-red-100": !!error,
            "opacity-50 cursor-not-allowed": disabled,
            "border-2 border-dashed cursor-pointer p-12": !previewUrl,
          },
          className
        ),
        style: {
          width: width === "auto" ? "100%" : `${width}px`,
          height: height === "auto" ? "100%" : `${height}px`,
        },
      })}
    >
      <input {...getInputProps()} data-testid="file-input" />
      {error ? (
        <ErrorState error={error} />
      ) : previewUrl ? (
        <ImagePreview src={previewUrl} />
      ) : (
        <ImageUploadEmptyState isDragActive={isDragActive} placeholder={placeholder} maxFileSize={maxFileSize} />
      )}
      {isUploading && (
        <div
          className={clsx(
            "absolute",
            "inset-0",
            "flex",
            "flex-col",
            "items-center",
            "justify-center",
            "gap-3",
            "bg-transparent",
            "backdrop-blur-sm",
            "px-4",
            "text-center"
          )}
          role="status"
          aria-live="polite"
        >
          <ProgressCircle
            size={ProgressCircleSize.XS}
            color={ProgressCircleColor.GREEN}
            infinite={true}
            showLabel={false}
          />
          <p className="m-0 text-xs font-medium text-text-base-primary">
            {uploadingText ?? t("image_upload.uploading_text")}
          </p>
        </div>
      )}
      {previewUrl && (
        <Button
          variant="ghost"
          size="md"
          onClick={handleRemove}
          className="absolute! top-1 right-1 p-2! shadow cursor-pointer z-100 group-hover:opacity-100 opacity-0 transition-opacity duration-300"
          aria-label={t("image_upload.button.remove_image")}
          type="button"
        >
          <Icon
            icon="icon-lt-delete-bin-line"
            size="md"
            extraClasses="text-red-200 hover:text-red-600 rounded-full"
            cursor="pointer"
          />
        </Button>
      )}
    </div>
  );
};

const ImageUploadEmptyState = ({
  isDragActive,
  placeholder,
  maxFileSize,
}: {
  isDragActive: boolean;
  placeholder?: string;
  maxFileSize: MaxFileSize;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Icon icon="icon-lt-upload-image-add-line" size="lg" extraClasses="text-platform-blue-950 mb-5" />
      <p className="m-0 text-base text-platform-blue-950">
        {isDragActive
          ? t("image_upload.drop_image_here")
          : placeholder || (
              <>
                {t("image_upload.drop_or")} <span className="text-text-base-active">{t("image_upload.browse")}</span>
              </>
            )}
      </p>
      <p className="m-0 text-sm text-blue-grey-600">
        {t("image_upload.max_file_size", {
          max_file_size: maxFileSize.value,
          unit: maxFileSize.unit,
        })}
      </p>
    </>
  );
};

const ErrorState = ({ error }: { error: string }) => {
  const { t } = useTranslation();

  return (
    <>
      <Icon icon="icon-lt-error-warning-line" size="lg" extraClasses="text-red-600 mb-3" />
      <p className="text-sm font-medium m-0! text-red-600">
        {error} <span className="text-text-base-active">{t("image_upload.try_again")}</span>
      </p>
    </>
  );
};

const ImagePreview = ({ src }: { src: string }) => {
  const { t } = useTranslation();

  return (
    <div className="relative h-full w-auto overflow-hidden rounded-lg">
      <img src={src} alt={t("image_upload.preview.alt")} className="h-full w-auto object-cover rounded" />
    </div>
  );
};
